import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import {
  getChannels,
  getNews,
  getRadios,
  rssFeed,
  type FirestoreLike,
} from "@orvis/data";
import type {
  CountryConfig,
  NewsFeedDoc,
  NewsItem,
  StreamingDefinitionV2,
} from "@orvis/core";

/** Parsed `publishedAt` in ms; missing/unparseable → -Infinity (sorts last). */
function publishedTime(item: NewsItem): number {
  const t = Date.parse(item.publishedAt ?? "");
  return Number.isNaN(t) ? -Infinity : t;
}

/**
 * Newest first. RSS `pubDate` is RFC-822 ("Mon, 13 Oct …"), so comparing the
 * raw strings would order by weekday name — parse before comparing.
 */
function byPublishedAtDesc(a: NewsItem, b: NewsItem): number {
  const ta = publishedTime(a);
  const tb = publishedTime(b);
  return ta === tb ? 0 : tb > ta ? 1 : -1;
}

/**
 * All data reads flow through here. Anything that talks to Firestore or a
 * feed goes through `@orvis/data` — components never see the network.
 *
 * `staleTime` values are conservative:
 *   - streaming catalogs turn over slowly (daily updater) → 5 min
 *   - Firestore news doc rarely present today → 5 min
 *   - RSS news is fetched on-demand → 5 min so users can pull-to-refresh
 */

const FIVE_MIN = 5 * 60 * 1000;

export function useChannels(db: FirestoreLike, country: string): UseQueryResult<StreamingDefinitionV2 | undefined> {
  return useQuery({
    queryKey: ["channels", country],
    queryFn: () => getChannels(db, country),
    staleTime: FIVE_MIN,
  });
}

export function useRadios(db: FirestoreLike, country: string): UseQueryResult<StreamingDefinitionV2 | undefined> {
  return useQuery({
    queryKey: ["radio", country],
    queryFn: () => getRadios(db, country),
    staleTime: FIVE_MIN,
  });
}

/**
 * News = optional Firestore doc first, falling back to the country's
 * hard-coded RSS feeds if no doc is present.
 *
 * The Firestore doc is a plan Phase 5+ deliverable; today the app runs
 * on the RSS fallback, which matches the legacy Expo behavior exactly.
 */
export function useNews(db: FirestoreLike, country: CountryConfig): UseQueryResult<{ items: NewsItem[]; source: NewsFeedDoc | undefined }> {
  return useQuery({
    queryKey: ["news", country.code],
    staleTime: FIVE_MIN,
    queryFn: async () => {
      const fromDoc = await getNews(db, country.code);
      if (fromDoc?.open && fromDoc.open.length > 0) {
        return { items: fromDoc.open, source: fromDoc };
      }

      // RSS fallback — hit every enabled source in the country config in
      // parallel and merge, most recent first.
      const enabled = country.newsSources.filter((s) => s.enabled !== false);
      const perFeed = await Promise.all(
        enabled.map(async (src) => {
          try {
            return await rssFeed(src.name ?? src.id).fetch({ url: src.feedUrl });
          } catch (err) {
            console.warn(`news source ${src.id} failed:`, err);
            return [] as NewsItem[];
          }
        })
      );
      const items = perFeed.flat();
      items.sort(byPublishedAtDesc);
      return { items, source: fromDoc };
    },
  });
}
